import type { Say } from "./tool-labels";

/**
 * Um arquivo que o turno ENTREGA — no fio, e o suficiente para desenhá-lo.
 *
 * ── O BURACO QUE ISTO FECHA ───────────────────────────────────────────────
 *
 * Pedido um arquivo no chat do painel, o Ben respondia que "aqui só sai texto" e
 * colava o conteúdo na conversa. A rota dos bytes sempre existiu
 * (`GET /v3/files/:id/url`, `GET /v3/files/blob/:id`); o que não existia era a
 * conversa APONTAR para ela. Este arquivo é o apontamento.
 *
 * ── A FORMA, QUE É A DO SERVIDOR ──────────────────────────────────────────
 *
 * `{ id, name, mime, sizeBytes }` — o `ToolAttachment` do lote do servidor
 * (`agent/application/ports/tool.ports.ts`), e o mesmo `sizeBytes` que o
 * `FileDto` e a `UploadPort` já dizem. Uma palavra para um fato, no painel
 * inteiro.
 *
 * Este leitor também aceita `bytes`, `size`, `mimeType` e o nome tirado de `path`,
 * que são os apelidos com que o mesmo fato aparece nas outras rotas de arquivo.
 * Não são um segundo contrato: é UM leitor, num lugar só, que sabe que os dois
 * vocabulários da casa nomeiam a mesma coisa.
 *
 * ── POR QUE O `url` DO SERVIDOR É IGNORADO ────────────────────────────────
 *
 * Ele vem no fio e é DESCARTADO aqui, de propósito, e o próprio servidor diz por
 * quê no `ToolAttachment`: "a signed one EXPIRES — a thread reopened next week
 * must re-resolve from `id`". Uma conversa reaberta amanhã carregaria um endereço
 * vencido e um botão que não abre nada.
 *
 * O endereço sai da `UploadPort`, que é o único lugar do painel que monta URL de
 * arquivo (I-S4) — a mesma porta do avatar e dos widgets. Guardar o `url` daria
 * um segundo lugar decidindo onde os bytes moram, e já houve o caso da URL do
 * chat público montada em três lugares enquanto o comentário jurava que era um só.
 */
export interface TurnAttachment {
	readonly id: string;
	/**
	 * What the file is called — EMPTY when the wire carried no name.
	 *
	 * Empty and not the word "file": this module has no locale, and a fallback
	 * invented here would be a name the panel could not translate. The screen says
	 * `attach.file` in its own language, which is also the only place that knows
	 * which language that is.
	 */
	readonly name: string;
	/** Pode chegar vazio, e chegar vazio é uma resposta válida — ver `kindOf`. */
	readonly mime: string;
	readonly sizeBytes: number;
	/**
	 * O QUE O ÁUDIO DIZ, quando o servidor sabia — a voz que o Ben gerou nasceu
	 * de um texto, e é esse texto. Vai por baixo do tocador, como na caixa de
	 * entrada: quem está em reunião lê a nota de voz em vez de a ouvir.
	 *
	 * AUSENTE em tudo o resto — e opcional, ao contrário de `name` e `mime`, que
	 * chegam vazios: aqueles são perguntas que todo arquivo responde (mal, às
	 * vezes); esta é uma pergunta que só um áudio tem. `ToolAttachment` no
	 * servidor diz o mesmo, e um contrato tem dois lados.
	 */
	readonly transcript?: string;
}

/**
 * Como o anexo se desenha.
 *
 * Duas formas e não seis: imagem se vê, e todo o resto é um cartão com nome, tipo,
 * peso e os dois gestos. Vídeo cai no cartão junto com o PDF — abrir toca o
 * arquivo na aba do navegador.
 *
 * ÁUDIO é a excepção, e foi decisão do dono (20/08): toca onde está, com a
 * transcrição por baixo. `kindOf` não a nomeia porque um áudio ainda TEM o
 * cartão — o tocador vem acima dele, não no lugar dele; quem o decide é o
 * `ChatAttachment`, olhando o mime.
 */
export type AttachmentKind = "image" | "file";

export function kindOf(mime: string): AttachmentKind {
	return mime.startsWith("image/") ? "image" : "file";
}

/** Um anexo do fio, ou `null` quando não há o que desenhar. */
export function toAttachment(value: unknown): TurnAttachment | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	const raw = value as Record<string, unknown>;
	// Um quadro que embrulhe o arquivo (`{ file: {...} }`) é lido pelo miolo: o
	// embrulho é do transporte, o fato é o arquivo.
	const source =
		text(raw, "id", "fileId") === "" && isObject(raw.file)
			? (raw.file as Record<string, unknown>)
			: raw;

	const id = text(source, "id", "fileId");
	if (!id) return null;

	const path = text(source, "path");
	return {
		id,
		name: text(source, "name", "filename", "fileName") || basename(path),
		mime: text(source, "mime", "mimeType"),
		sizeBytes: count(source, "sizeBytes", "bytes", "size"),
		...spokenWords(source),
	};
}

/** Uma lista do fio, com o lixo descartado em silêncio. */
export function attachmentsFrom(value: unknown): TurnAttachment[] {
	if (!Array.isArray(value)) return [];
	return value
		.map(toAttachment)
		.filter((file): file is TurnAttachment => file !== null)
		.reduce<TurnAttachment[]>((all, file) => addAttachment(all, file), []);
}

/**
 * Acrescenta sem repetir.
 *
 * O mesmo arquivo pode chegar duas vezes: uma no evento `attachment`, no instante
 * em que a tool o entrega, e outra na lista do quadro `done`. Um anexo é conhecido
 * pelo ID, venha no quadro que vier — é uma regra só, e é ela que permite ao
 * servidor mandar por qualquer um dos dois caminhos sem a tela desenhar em dobro.
 */
export function addAttachment(known: TurnAttachment[], file: TurnAttachment): TurnAttachment[] {
	return known.some((f) => f.id === file.id) ? known : [...known, file];
}

/**
 * The TYPE, said short: "PDF", "CSV", "XLSX".
 *
 * Never empty and never the raw mime — `application/vnd.openxmlformats-officedocument…`
 * on a card's label informs nobody. A type nobody knows falls back to the name's
 * extension, and a file without even that still says "file": the rule is that an
 * attachment NEVER disappears from the screen for not having been recognised.
 */
const TYPE_LABEL: Record<string, string> = {
	"application/pdf": "PDF",
	"application/json": "JSON",
	"application/zip": "ZIP",
	"application/x-zip-compressed": "ZIP",
	"text/csv": "CSV",
	"text/plain": "TXT",
	"text/markdown": "MD",
	"text/html": "HTML",
	"application/vnd.ms-excel": "XLS",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
	"application/msword": "DOC",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
};

export function typeLabel(file: TurnAttachment, say: Say): string {
	const known = TYPE_LABEL[file.mime.toLowerCase()];
	if (known) return known;

	const extension = /\.([a-z0-9]{1,8})$/i.exec(file.name)?.[1];
	if (extension) return extension.toUpperCase();

	// `octet-stream` is the mime for "I do not know what this is"; repeating it on
	// screen would report the server's ignorance instead of naming the file. The
	// last word is the reader's, so it comes from the caller's dictionary.
	const subtype = file.mime.split("/")[1] ?? "";
	return subtype && subtype !== "octet-stream" ? subtype.toUpperCase() : say("attach.file");
}

function isObject(value: unknown): boolean {
	return !!value && typeof value === "object" && !Array.isArray(value);
}

function text(source: Record<string, unknown>, ...keys: string[]): string {
	for (const key of keys) {
		const value = source[key];
		if (typeof value === "string" && value) return value;
	}
	return "";
}

function count(source: Record<string, unknown>, ...keys: string[]): number {
	for (const key of keys) {
		const value = source[key];
		if (typeof value === "number" && Number.isFinite(value) && value >= 0) return value;
	}
	return 0;
}

/** A transcrição, quando veio — e a chave ausente quando não, nunca uma vazia. */
function spokenWords(source: Record<string, unknown>): { transcript?: string } {
	const said = text(source, "transcript", "transcription");
	return said ? { transcript: said } : {};
}

function basename(path: string): string {
	return path.split("/").pop() ?? "";
}
